import { Controller } from "@hotwired/stimulus"

// Horizontal slider/carousel for store reviews on homepage
// Supports auto-scroll, navigation buttons, touch/drag, and modal
export default class extends Controller {
  static targets = ["track", "prevBtn", "nextBtn", "modal", "modalBody", "reviewsData"]
  static values = {
    autoScroll: { type: Number, default: 10000 }, // ms, 0 to disable
    currentIndex: { type: Number, default: 0 }
  }

  connect() {
    this.cardWidth = 0
    this.gap = 16 // matches CSS gap
    this.visibleCards = 1
    this.reviews = []

    // Load reviews data for modal
    if (this.hasReviewsDataTarget) {
      try {
        this.reviews = JSON.parse(this.reviewsDataTarget.textContent)
      } catch (e) {
        console.error('Failed to parse reviews data:', e)
      }
    }

    this.calculateDimensions()
    this.updateNavButtons()
    this.startAutoScroll()

    // Recalculate on resize
    this.resizeHandler = this.handleResize.bind(this)
    window.addEventListener('resize', this.resizeHandler)

    // Pause auto-scroll on hover
    this.element.addEventListener('mouseenter', () => this.pauseAutoScroll())
    this.element.addEventListener('mouseleave', () => this.resumeAutoScroll())

    // Touch support
    this.setupTouchSupport()

    // Close modal on Escape key
    this.escapeHandler = this.handleEscape.bind(this)
    document.addEventListener('keydown', this.escapeHandler)
  }

  disconnect() {
    this.stopAutoScroll()
    window.removeEventListener('resize', this.resizeHandler)
    document.removeEventListener('keydown', this.escapeHandler)
  }

  calculateDimensions() {
    if (!this.hasTrackTarget) return

    const cards = this.trackTarget.querySelectorAll('.slider-card')
    if (cards.length === 0) return

    this.totalCards = cards.length
    this.cardWidth = cards[0].offsetWidth

    const trackWrapper = this.trackTarget.parentElement
    const availableWidth = trackWrapper.offsetWidth - 96 // subtract padding for nav buttons

    this.visibleCards = Math.floor(availableWidth / (this.cardWidth + this.gap))
    this.visibleCards = Math.max(1, this.visibleCards)
    this.maxIndex = Math.max(0, this.totalCards - this.visibleCards)
  }

  handleResize() {
    this.calculateDimensions()
    this.currentIndexValue = Math.min(this.currentIndexValue, this.maxIndex)
    this.scrollToIndex(this.currentIndexValue)
    this.updateNavButtons()
  }

  prev() {
    if (this.currentIndexValue > 0) {
      this.currentIndexValue--
      this.scrollToIndex(this.currentIndexValue)
      this.updateNavButtons()
    }
  }

  next() {
    if (this.currentIndexValue < this.maxIndex) {
      this.currentIndexValue++
      this.scrollToIndex(this.currentIndexValue)
      this.updateNavButtons()
    } else if (this.autoScrollValue > 0) {
      // Loop back to start for auto-scroll
      this.currentIndexValue = 0
      this.scrollToIndex(0)
      this.updateNavButtons()
    }
  }

  scrollToIndex(index) {
    if (!this.hasTrackTarget) return

    const offset = index * (this.cardWidth + this.gap)
    this.trackTarget.style.transform = `translateX(-${offset}px)`
  }

  updateNavButtons() {
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.disabled = this.currentIndexValue === 0
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.disabled = this.currentIndexValue >= this.maxIndex
    }
  }

  startAutoScroll() {
    if (this.autoScrollValue > 0 && this.totalCards > this.visibleCards) {
      this.autoScrollInterval = setInterval(() => {
        this.next()
      }, this.autoScrollValue)
    }
  }

  stopAutoScroll() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval)
      this.autoScrollInterval = null
    }
  }

  pauseAutoScroll() {
    this.isPaused = true
    this.stopAutoScroll()
  }

  resumeAutoScroll() {
    this.isPaused = false
    this.startAutoScroll()
  }

  setupTouchSupport() {
    if (!this.hasTrackTarget) return

    let startX = 0
    let currentX = 0
    let isDragging = false

    this.trackTarget.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX
      isDragging = true
      this.pauseAutoScroll()
    }, { passive: true })

    this.trackTarget.addEventListener('touchmove', (e) => {
      if (!isDragging) return
      currentX = e.touches[0].clientX
    }, { passive: true })

    this.trackTarget.addEventListener('touchend', () => {
      if (!isDragging) return
      isDragging = false

      const diff = startX - currentX
      const threshold = 50

      if (diff > threshold) {
        this.next()
      } else if (diff < -threshold) {
        this.prev()
      }

      this.resumeAutoScroll()
    })
  }

  // Modal methods
  showModal(event) {
    event.preventDefault()
    event.stopPropagation()

    const index = parseInt(event.currentTarget.dataset.reviewIndex, 10)
    const review = this.reviews[index]

    if (!review || !this.hasModalTarget || !this.hasModalBodyTarget) return

    // Render modal content
    this.modalBodyTarget.innerHTML = this.renderModalContent(review)

    // Show modal
    this.modalTarget.classList.add('is-open')
    document.body.style.overflow = 'hidden'

    // Focus trap
    this.modalTarget.querySelector('.review-modal-close')?.focus()
  }

  closeModal() {
    if (!this.hasModalTarget) return

    this.modalTarget.classList.remove('is-open')
    document.body.style.overflow = ''
  }

  handleEscape(event) {
    if (event.key === 'Escape') {
      this.closeModal()
    }
  }

  renderModalContent(review) {
    const stars = this.renderStars(review.rating)
    const photos = this.renderModalPhotos(review.photos)
    const productName = review.product_name ? `
      <div class="modal-product-name">
        <i class="fa-solid fa-box-open"></i>
        ${this.escapeHtml(review.product_name)}
      </div>
    ` : ''

    return `
      <div class="modal-stars">${stars}</div>
      ${productName}
      ${review.comments ? `<div class="modal-text">${this.escapeHtml(review.comments)}</div>` : ''}
      ${photos}
      <div class="modal-author">
        <div class="modal-author-name">${this.escapeHtml(review.author_name)}</div>
        <div class="modal-verified-badge">
          <i class="fa-solid fa-circle-check"></i>
          Verified Customer
        </div>
        ${review.author_location ? `<div class="modal-author-location">${this.escapeHtml(review.author_location)}</div>` : ''}
        <div class="modal-date">${review.review_date}</div>
      </div>
    `
  }

  renderStars(rating) {
    let html = ''
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        html += '<i class="fa-solid fa-star"></i>'
      } else {
        html += '<i class="fa-regular fa-star"></i>'
      }
    }
    return html
  }

  renderModalPhotos(photos) {
    if (!photos || photos.length === 0) return ''

    const photoHtml = photos.map(photo => {
      const url = typeof photo === 'object' ? photo.url : photo
      if (!url) return ''
      return `<img src="${this.escapeHtml(url)}" alt="Review photo" class="modal-photo">`
    }).join('')

    return photoHtml ? `<div class="modal-photos">${photoHtml}</div>` : ''
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}

